import { KatAlert, KatButton, KatDropdown, KatInput, KatSpinner, KatTab, KatTabs, KatToggle, KatCheckbox, KatModal } from '@amzn/katal-react';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { SITE_ID_PARAM_NAME } from 'src/core/config/navigationRoutes';
import { useFCConfig } from 'src/features/hooks/useFCConfig';
import { useGTRBConfig } from 'src/features/hooks/useGTRBConfig';
import { useSetGTRBFlag } from 'src/features/hooks/useSetGTRBFlag';
import { ErrorBoundary } from 'src/features/components/granularSLA/ErrorBoundary';
import { usePCDetails } from 'src/features/hooks/usePCDetails';
import { useUser } from 'src/utils/UserContext';
import { updateFCConfig, triggerCategorizationGeneration, updatePCOverrides, deletePCOverride, getFCConfig, getGTRBConfig, GetFCConfigResponse, GetGTRBConfigResponse } from 'src/utils/Queries';
import { ProcessPathMappingEntry } from 'src/utils/Queries';
import { GTRBFlagStatus } from 'src/utils/Queries';
import 'src/features/styles/shared/colors.css';
import './FCConfigPage.css';

interface PCColumnProps {
    pcName: string;
    ppList: ProcessPathMappingEntry[];
    editMode?: boolean;
    isNew?: boolean;
    overrides?: Record<string, string>;
    pendingRemovals?: string[];
    onDrop?: ( ppName: string, targetPC: string ) => void;
    onRemoveOverride?: ( ppName: string ) => void;
}

const PCColumn: React.FC<PCColumnProps> = ({ pcName, ppList, editMode, isNew, overrides, pendingRemovals, onDrop, onRemoveOverride }) => {
    const [dragOver, setDragOver] = useState(false);
    const sorted = useMemo(() => [...ppList].sort((a, b) => a.processPathName.localeCompare(b.processPathName)), [ppList]);

    const handleDragOver = useCallback(( e: React.DragEvent ) => {
        e.preventDefault();
        setDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => {
        setDragOver(false);
    }, []);

    const handleDrop = useCallback(( e: React.DragEvent ) => {
        e.preventDefault();
        setDragOver(false);
        const ppName = e.dataTransfer.getData('text/plain');
        if (ppName && onDrop) {
            onDrop(ppName, pcName);
        }
    }, [pcName, onDrop]);

    const handleDragStart = useCallback(( e: React.DragEvent, ppName: string ) => {
        e.dataTransfer.setData('text/plain', ppName);
        e.dataTransfer.effectAllowed = 'move';
    }, []);

    const isOverridden = ( ppName: string ) => overrides && ppName in overrides;

    return (
        <div
            className={`pc-column ${editMode ? 'pc-column-editable' : ''} ${isNew ? 'pc-column-new' : ''} ${dragOver ? 'pc-column-drag-over' : ''}`}
            onDragOver={editMode ? handleDragOver : undefined}
            onDragLeave={editMode ? handleDragLeave : undefined}
            onDrop={editMode ? handleDrop : undefined}
        >
            <div className="pc-column-header">
                <span className="pc-column-name">{pcName}</span>
                <span className="pc-column-count">{ppList.length}</span>
            </div>
            <div className="pc-column-body">
                {sorted.map(pp => (
                    <div
                        key={pp.processPathName}
                        className={`pp-chip ${pp.isFamilyOverridden ? 'pp-chip-overridden' : ''} ${(isOverridden(pp.processPathName) || pp.isPCOverridden) ? 'pp-chip-pc-overridden' : ''} ${editMode ? 'pp-chip-draggable' : ''} ${pendingRemovals?.includes(pp.processPathName) ? 'pp-chip-pending-removal' : ''}`}
                        draggable={editMode}
                        onDragStart={editMode ? (e) => handleDragStart(e, pp.processPathName) : undefined}
                    >
                        <div className="pp-chip-top">
                            <span className="pp-chip-name">{pp.processPathName}</span>
                            {pp.isFamilyOverridden && (
                                <span className="override-badge" data-tooltip={"Family overridden from " + pp.processPathFamily}>⚠</span>
                            )}
                            {isOverridden(pp.processPathName) && (
                                <span className="override-badge override-badge-pc" data-tooltip="PC Override active">✏️</span>
                            )}
                            {pp.isPCOverridden && !isOverridden(pp.processPathName) && (
                                <span className="override-capsule">Overridden</span>
                            )}
                        </div>
                        <div className="pp-chip-bottom">
                            {editMode && (isOverridden(pp.processPathName) || pp.isPCOverridden) && onRemoveOverride && (
                                <button
                                    className="pp-chip-remove-override"
                                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); onRemoveOverride(pp.processPathName); }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    draggable={false}
                                    title="Remove override — PP returns to system default"
                                >✕</button>
                            )}
                        </div>
                    </div>
                ))}
                {editMode && ppList.length === 0 && (
                    <div className="pc-column-empty-drop">Drop PPs here</div>
                )}
            </div>
        </div>
    );
};

/**
 * Extract a human-readable message from a rejection. The signed APIGatewayClients fetch wrapper rejects
 * with the parsed JSON error body (a plain object like { message } / { errorMessage } / { __type }), not
 * always a JS Error — so `String(e)` on it yields the useless literal "[object Object]". Prefer the known
 * message-ish fields, then fall back to a JSON dump, so a failed row shows the real backend reason.
 */
const extractErrorMessage = (e: any): string => {
    if (e == null) {
        return 'Unknown error';
    }
    if (typeof e === 'string') {
        return e;
    }
    const msg = e.message || e.errorMessage || e.Message || e.__type || e.code;
    if (msg) {
        return String(msg);
    }
    try {
        return JSON.stringify(e);
    } catch {
        return String(e);
    }
};

const FCConfigPage: React.FC = () => {
    const { user } = useUser();
    const { [SITE_ID_PARAM_NAME]: siteId } = useParams();
    const { data, isLoading, error, refetch } = useFCConfig(siteId);
    const { data: pcDetails, isLoading: pcDetailsLoading, refetch: refetchPCDetails } = usePCDetails(siteId);

    const { data: gtrbData, isLoading: gtrbLoading, error: gtrbError } = useGTRBConfig(user?.isDevAdmin ? siteId : undefined);
    const setGtrbFlagMutation = useSetGTRBFlag(siteId);
    const [gtrbTicketUrl, setGtrbTicketUrl] = useState('');
    const [gtrbPendingKey, setGtrbPendingKey] = useState<string | null>(null);
    // Copy-configuration-from-another-FC feature state.
    const [copySourceFc, setCopySourceFc] = useState('');
    const [copyScopePicking, setCopyScopePicking] = useState(true);
    const [copyScopeHov, setCopyScopeHov] = useState(true);
    const [copyScopeGtrb, setCopyScopeGtrb] = useState(true);
    const [copyScopeFcType, setCopyScopeFcType] = useState(true);
    const [copyTicketUrl, setCopyTicketUrl] = useState('');
    const [copyLoading, setCopyLoading] = useState(false);
    const [copyApplying, setCopyApplying] = useState(false);
    const [copyError, setCopyError] = useState<string | null>(null);
    const [copyStatus, setCopyStatus] = useState<string | null>(null);
    // Loaded source config for the diff (null until "Load diff" is run).
    const [copySourceConfig, setCopySourceConfig] = useState<{ fc: GetFCConfigResponse | null; gtrb: GetGTRBConfigResponse | null } | null>(null);
    const [copyModalOpen, setCopyModalOpen] = useState(false);
    // Per-row apply status while copying (keyed by diff row key): 'saving' | 'done' | 'error'.
    const [copyRowStatus, setCopyRowStatus] = useState<Record<string, 'saving' | 'done' | 'error'>>({});
    const [copyRowErrors, setCopyRowErrors] = useState<Record<string, string>>({});
    const [gtrbRowError, setGtrbRowError] = useState<{ key: string; message: string } | null>(null);

    // GTRB Phase 2a — the 8 editable featureLaunchConfig flags. Declared here (before any early
    // return) to satisfy the Rules of Hooks.
    const GTRB_EDITABLE_KEYS = useMemo(() => new Set<string>([
        'PCGEN_GROCERY_RULES',
        'PCGEN_DISABLE_SHIP_OPTIONS',
        'GSLA_PC_WHT_EXPANSION',
        'GSLA_DISABLE_DEFAULT_FC_PC',
        'FOURIER_ENABLE_BREAK_AWARE_CAPACITY',
        'LAGRANGE_GENERATE_GRANULAR_MODEL',
        'LAGRANGE_USE_CPP_COMMON_DATA_FOR_PP_GROUPS',
        'LAGRANGE_SYNC_GRANULAR_AND_CORA_MODELS',
        'LAGRANGE_CDF_ENABLED',
        'LAGRANGE_USE_HEDGED_CAPACITY',
    ]), []);

    // GSLA flags are SSD-specific — grouped separately at the bottom (only meaningful for SSD sites).
    const GTRB_SSD_KEYS = useMemo(() => new Set<string>([
        'GSLA_PC_WHT_EXPANSION',
        'GSLA_DISABLE_DEFAULT_FC_PC',
    ]), []);

    // The backend returns ALL flag data; the UI decides what to render. These keys are surfaced
    // elsewhere (FOCAL capsule up top, fcType dropdown) or intentionally not shown, so they are not
    // rendered as rows in the flag list.
    const GTRB_HIDDEN_KEYS = useMemo(() => new Set<string>([
        'PCGEN_RULE_REFRESH',
        'CDS_R2F_ENABLED',
        'CDS_FC_ATTRIBUTES',
    ]), []);

    const GTRB_FC_TYPES = useMemo(() => ['SSD', 'ARS', 'TNS'], []);

    const isGtrbTicketValid = gtrbTicketUrl.trim().startsWith('https://');

    const handleGtrbToggle = useCallback((flagKey: string, nextEnabled: boolean) => {
        if (!siteId || !user?.login || !isGtrbTicketValid) {
            return;
        }
        setGtrbRowError(null);
        setGtrbPendingKey(flagKey);
        setGtrbFlagMutation.mutate(
            { fcName: siteId, flagKey, enabled: nextEnabled, ticketUrl: gtrbTicketUrl.trim(), updatedBy: user.login },
            {
                onError: (err: any) => setGtrbRowError({ key: flagKey, message: extractErrorMessage(err) }),
                onSettled: () => setGtrbPendingKey(null),
            }
        );
    }, [siteId, user, isGtrbTicketValid, gtrbTicketUrl, setGtrbFlagMutation]);

    const handleGtrbFcTypeChange = useCallback((nextValue: string) => {
        if (!siteId || !user?.login || !isGtrbTicketValid || !nextValue) {
            return;
        }
        setGtrbRowError(null);
        setGtrbPendingKey('CDS_FC_TYPE');
        setGtrbFlagMutation.mutate(
            { fcName: siteId, flagKey: 'CDS_FC_TYPE', value: nextValue, ticketUrl: gtrbTicketUrl.trim(), updatedBy: user.login },
            {
                onError: (err: any) => setGtrbRowError({ key: 'CDS_FC_TYPE', message: extractErrorMessage(err) }),
                onSettled: () => setGtrbPendingKey(null),
            }
        );
    }, [siteId, user, isGtrbTicketValid, gtrbTicketUrl, setGtrbFlagMutation]);

    const renderGtrbFlagRow = useCallback((flag: GTRBFlagStatus) => {
        const isUnknown = flag.applicability === 'UNKNOWN';
        const isNotApplicable = flag.applicability === 'NOT_APPLICABLE';
        const stateClass = isUnknown
            ? 'gtrb-flag-chip-unknown'
            : isNotApplicable
                ? 'gtrb-flag-chip-na'
                : flag.enabled
                    ? 'gtrb-flag-chip-enabled'
                    : 'gtrb-flag-chip-disabled';
        const stateLabel = isUnknown
            ? 'Unknown'
            : isNotApplicable
                ? 'N/A'
                : flag.enabled
                    ? 'Enabled'
                    : 'Not enabled';
        // Every editable + APPLICABLE flag gets a toggle (default-all included): the toggle adds/removes
        // this FC from the allow- or block-list per-FC without changing the global default.
        const isEditable = GTRB_EDITABLE_KEYS.has(flag.key) && flag.applicability === 'APPLICABLE';
        const isRowPending = gtrbPendingKey === flag.key;
        return (
            <div key={flag.key} className="gtrb-flag-row">
                <div className="gtrb-flag-main">
                    <span className={`gtrb-flag-chip ${stateClass}`}>{stateLabel}</span>
                    <span className="gtrb-flag-label">{flag.label}</span>
                    <span className="gtrb-flag-system">{flag.owningSystem}</span>
                    {isEditable && (
                        <span className="gtrb-flag-toggle">
                            {isRowPending ? (
                                <KatSpinner size="small" />
                            ) : (
                                <KatToggle
                                    checked={!!flag.enabled}
                                    disabled={!isGtrbTicketValid || setGtrbFlagMutation.isLoading}
                                    onChange={(e: any) => handleGtrbToggle(flag.key, e.detail?.checked ?? !flag.enabled)}
                                />
                            )}
                        </span>
                    )}
                </div>
                <div className="gtrb-flag-detail">
                    {flag.value != null && <span className="gtrb-flag-value">{flag.value}</span>}
                    {flag.detail && <span className="gtrb-flag-detail-text">{flag.detail}</span>}
                </div>
                {gtrbRowError && gtrbRowError.key === flag.key && (
                    <div className="gtrb-flag-row-error">{gtrbRowError.message}</div>
                )}
            </div>
        );
    }, [GTRB_EDITABLE_KEYS, gtrbPendingKey, gtrbRowError, isGtrbTicketValid, setGtrbFlagMutation, handleGtrbToggle]);

    const isCopyTicketValid = copyTicketUrl.trim().startsWith('https://');

    // Fetch the source FC's config (PCGen + GTRB) for the diff. Does not mutate anything.
    const loadCopyDiff = useCallback(() => {
        const source = copySourceFc.trim().toUpperCase();
        setCopyError(null);
        setCopyStatus(null);
        setCopySourceConfig(null);
        if (!source) {
            setCopyError('Enter a source FC.');
            return;
        }
        if (source === (siteId || '').toUpperCase()) {
            setCopyError('Source FC must be different from the target FC.');
            return;
        }
        setCopyLoading(true);
        Promise.all([
            getFCConfig(source) as Promise<GetFCConfigResponse>,
            getGTRBConfig(source) as Promise<GetGTRBConfigResponse>,
        ])
            .then(([fc, gtrb]) => setCopySourceConfig({ fc: fc || null, gtrb: gtrb || null }))
            .catch((e: any) => setCopyError(`Could not load source FC ${source}: ${extractErrorMessage(e)}`))
            .finally(() => setCopyLoading(false));
    }, [copySourceFc, siteId]);

    // Compute the full in-scope comparison (all settings, matching + differing) + skipped GTRB flags.
    const copyDiff = useMemo(() => {
        type Row = { key: string; label: string; target: string; source: string; changed: boolean; apply?: () => Promise<unknown> };
        const rows: Row[] = [];
        const skipped: string[] = [];
        if (!copySourceConfig || !siteId) {
            return { rows, skipped };
        }
        const ticket = copyTicketUrl.trim();
        const updatedBy = user?.login || '';
        const srcFc = copySourceConfig.fc;
        const srcGtrb = copySourceConfig.gtrb;
        const tgtGtrb = gtrbData;

        // PICKING PCGen fields.
        if (copyScopePicking && srcFc) {
            const mSrc = srcFc.productionCapabilityMappingType || '', mTgt = data?.productionCapabilityMappingType || '';
            rows.push({
                key: 'PICKING_MAPPING', label: 'PICKING PC Mapping', target: mTgt || '(none)', source: mSrc || '(none)', changed: mSrc !== mTgt,
                apply: mSrc !== mTgt ? () => updateFCConfig({ fcName: siteId, productionCapabilityMappingType: srcFc.productionCapabilityMappingType, ticketUrl: ticket, updatedBy } as any) as any : undefined,
            });
            const gSrc = srcFc.categorizationGenerationType || '', gTgt = data?.categorizationGenerationType || '';
            rows.push({
                key: 'PICKING_RULES', label: 'PICKING Rule Generation', target: gTgt || '(none)', source: gSrc || '(none)', changed: gSrc !== gTgt,
                apply: gSrc !== gTgt ? () => updateFCConfig({ fcName: siteId, categorizationGenerationType: srcFc.categorizationGenerationType, ticketUrl: ticket, updatedBy } as any) as any : undefined,
            });
        }
        // HOV PCGen fields.
        if (copyScopeHov && srcFc) {
            const mSrc = srcFc.hovProductionCapabilityMappingType || '', mTgt = data?.hovProductionCapabilityMappingType || '';
            rows.push({
                key: 'HOV_MAPPING', label: 'HOV PC Mapping', target: mTgt || '(inherits)', source: mSrc || '(inherits)', changed: mSrc !== mTgt,
                apply: mSrc !== mTgt ? () => updateFCConfig({ fcName: siteId, hovProductionCapabilityMappingType: srcFc.hovProductionCapabilityMappingType, ticketUrl: ticket, updatedBy } as any) as any : undefined,
            });
            const gSrc = srcFc.hovCategorizationGenerationType || '', gTgt = data?.hovCategorizationGenerationType || '';
            rows.push({
                key: 'HOV_RULES', label: 'HOV Rule Generation', target: gTgt || '(inherits)', source: gSrc || '(inherits)', changed: gSrc !== gTgt,
                apply: gSrc !== gTgt ? () => updateFCConfig({ fcName: siteId, hovCategorizationGenerationType: srcFc.hovCategorizationGenerationType, ticketUrl: ticket, updatedBy } as any) as any : undefined,
            });
        }
        // FC type.
        if (copyScopeFcType && srcGtrb) {
            const srcType = srcGtrb.fcType || '';
            const tgtType = gtrbData?.fcType || '';
            const applicable = !!srcType && GTRB_FC_TYPES.includes(srcType);
            if (applicable) {
                rows.push({
                    key: 'CDS_FC_TYPE', label: 'FC type', target: tgtType || '(unset)', source: srcType, changed: srcType !== tgtType,
                    apply: srcType !== tgtType ? () => setGtrbFlagMutation.mutateAsync({ fcName: siteId, flagKey: 'CDS_FC_TYPE', value: srcType, ticketUrl: ticket, updatedBy }) : undefined,
                });
            }
            else if (srcType)
            {
                skipped.push(`FC type (source value "${srcType}" is not settable)`);
            }
        }
        // GTRB flags — only editable + APPLICABLE on BOTH FCs; otherwise inform via skipped.
        if (copyScopeGtrb && srcGtrb?.flags) {
            srcGtrb.flags.forEach(srcFlag => {
                if (!GTRB_EDITABLE_KEYS.has(srcFlag.key)) {
                    return;
                }
                const tgtFlag = tgtGtrb?.flags?.find(f => f.key === srcFlag.key);
                const srcApplicable = srcFlag.applicability === 'APPLICABLE';
                const tgtApplicable = tgtFlag?.applicability === 'APPLICABLE';
                if (!srcApplicable || !tgtApplicable) {
                    skipped.push(`${srcFlag.label} (not applicable on ${!srcApplicable ? 'source' : 'target'})`);
                    return;
                }
                const nextEnabled = !!srcFlag.enabled;
                const changed = nextEnabled !== !!tgtFlag?.enabled;
                rows.push({
                    key: srcFlag.key, label: srcFlag.label,
                    target: tgtFlag?.enabled ? 'Enabled' : 'Not enabled', source: nextEnabled ? 'Enabled' : 'Not enabled', changed,
                    apply: changed ? () => setGtrbFlagMutation.mutateAsync({ fcName: siteId, flagKey: srcFlag.key, enabled: nextEnabled, ticketUrl: ticket, updatedBy }) : undefined,
                });
            });
        }
        return { rows, skipped };
    }, [copySourceConfig, siteId, copyTicketUrl, user, data, gtrbData, copyScopePicking, copyScopeHov, copyScopeGtrb, copyScopeFcType, GTRB_EDITABLE_KEYS, GTRB_FC_TYPES, setGtrbFlagMutation]);

    // Only the changed rows are actually written.
    const copyChangedRows = useMemo(() => copyDiff.rows.filter(r => r.changed && r.apply), [copyDiff]);

    const applyCopy = useCallback(() => {
        if (!isCopyTicketValid || copyChangedRows.length === 0) {
            return;
        }
        setCopyApplying(true);
        setCopyError(null);
        setCopyStatus(null);
        setCopyRowStatus({});
        setCopyRowErrors({});
        const rows = copyChangedRows;
        const sourceLabel = copySourceFc.trim().toUpperCase();
        let ok = 0;
        // Apply sequentially (chained promises) so each individual write is saved and ticked off one-by-one.
        rows.reduce(
            (chain, row) => chain.then(() => {
                setCopyRowStatus(prev => ({ ...prev, [row.key]: 'saving' }));
                return (row.apply as () => Promise<unknown>)()
                    .then(() => {
                        ok += 1;
                        setCopyRowStatus(prev => ({ ...prev, [row.key]: 'done' }));
                    })
                    .catch((e: any) => {
                        setCopyRowStatus(prev => ({ ...prev, [row.key]: 'error' }));
                        setCopyRowErrors(prev => ({ ...prev, [row.key]: extractErrorMessage(e) }));
                    });
            }),
            Promise.resolve() as Promise<unknown>,
        ).then(() => {
            setCopyApplying(false);
            const failed = rows.length - ok;
            if (failed > 0) {
                setCopyError(`Copied ${ok} of ${rows.length} setting(s) from ${sourceLabel}; ${failed} failed. See the per-row details.`);
            } else {
                setCopyStatus(`Copied ${ok} of ${rows.length} setting(s) from ${sourceLabel}.`);
            }
            refetch();
            refetchPCDetails();
        });
    }, [isCopyTicketValid, copyChangedRows, copySourceFc, refetch, refetchPCDetails]);

    const openCopyModal = useCallback(() => {
        setCopyModalOpen(true);
        setCopySourceConfig(null);
        setCopyError(null);
        setCopyStatus(null);
        setCopyRowStatus({});
        setCopyRowErrors({});
        setCopyTicketUrl('');
    }, []);

    const closeCopyModal = useCallback(() => {
        setCopyModalOpen(false);
        setGtrbFlagMutation.reset();
    }, [setGtrbFlagMutation]);
    const [selectedTab, setSelectedTab] = useState(user?.isDevAdmin ? 'configuration' : 'pc-mapping');
    const [configTypeTab, setConfigTypeTab] = useState<'PICKING' | 'HOV'>('PICKING');
    const [selectedMappingType, setSelectedMappingType] = useState<string>('');
    const [selectedGenerationType, setSelectedGenerationType] = useState<string>('');
    const [selectedHovMappingType, setSelectedHovMappingType] = useState<string>('INHERIT_FROM_PICKING');
    const [selectedHovGenerationType, setSelectedHovGenerationType] = useState<string>('INHERIT_FROM_PICKING');
    const [ticketUrl, setTicketUrl] = useState<string>('');
    const [saveStatus, setSaveStatus] = useState<{ success: boolean; message: string } | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);

    // Edit mode state for PC-PP overrides
    const [editMode, setEditMode] = useState(false);
    const [pendingOverrides, setPendingOverrides] = useState<Record<string, string>>({});
    const [newPCName, setNewPCName] = useState('');
    const [showNewPCInput, setShowNewPCInput] = useState(false);
    const [overrideTicketUrl, setOverrideTicketUrl] = useState('');
    const [isSavingOverrides, setIsSavingOverrides] = useState(false);
    const [createdPCs, setCreatedPCs] = useState<string[]>([]);
    const [recentlyRemovedPPs, setRecentlyRemovedPPs] = useState<string[]>([]);

    useEffect(() => {
        if (data) {
            setSelectedMappingType(data.productionCapabilityMappingType || '');
            setSelectedGenerationType(data.categorizationGenerationType || '');
            setSelectedHovMappingType(data.hovProductionCapabilityMappingType || 'INHERIT_FROM_PICKING');
            setSelectedHovGenerationType(data.hovCategorizationGenerationType || 'INHERIT_FROM_PICKING');
        }
    }, [data]);

    const pcGroupedMappings = useMemo(() => {
        if (!pcDetails?.processPathMappings) return {};
        const grouped: Record<string, ProcessPathMappingEntry[]> = {};
        pcDetails.processPathMappings.forEach(pp => {
            // Hide PPs that were just un-overridden (optimistic removal until pipeline propagates)
            if (recentlyRemovedPPs.includes(pp.processPathName)) return;
            const pc = pp.productionCapability || 'Unknown';
            if (!grouped[pc]) grouped[pc] = [];
            grouped[pc].push(pp);
        });
        return grouped;
    }, [pcDetails, recentlyRemovedPPs]);

    const totalPPs = pcDetails?.processPathMappings?.length || 0;
    const totalPCs = Object.keys(pcGroupedMappings).length;

    // In edit mode, apply pending overrides to the view (preview of what will be saved)
    const editModeGroupedMappings = useMemo(() => {
        const base = { ...pcGroupedMappings };

        // Add user-created empty PCs
        createdPCs.forEach(pc => {
            if (!base[pc]) base[pc] = [];
        });

        if (!editMode || Object.keys(pendingOverrides).length === 0) return base;

        // Deep copy
        const result: Record<string, ProcessPathMappingEntry[]> = {};
        Object.entries(base).forEach(([pc, pps]) => {
            result[pc] = pps.filter(pp => !(pp.processPathName in pendingOverrides));
        });
        // Add overridden PPs to their target PCs
        Object.entries(pendingOverrides).forEach(([ppName, targetPC]) => {
            const originalPP = pcDetails?.processPathMappings?.find(pp => pp.processPathName === ppName);
            if (originalPP) {
                if (!result[targetPC]) result[targetPC] = [];
                result[targetPC].push(originalPP);
            }
        });
        // Remove empty PCs that existed before (not user-created)
        Object.keys(result).forEach(pc => {
            if (result[pc].length === 0 && pcGroupedMappings[pc] && !createdPCs.includes(pc) && !Object.values(pendingOverrides).includes(pc)) {
                delete result[pc];
            }
        });
        return result;
    }, [editMode, pendingOverrides, pcGroupedMappings, pcDetails, createdPCs]);

    const handleDrop = useCallback(( ppName: string, targetPC: string ) => {
        // Find current PC for this PP
        const currentPC = pcDetails?.processPathMappings?.find(pp => pp.processPathName === ppName)?.productionCapability;
        if (currentPC === targetPC) {
            // Dropped in same column — remove override if exists
            const updated = { ...pendingOverrides };
            delete updated[ppName];
            setPendingOverrides(updated);
        } else {
            setPendingOverrides(prev => ({ ...prev, [ppName]: targetPC }));
        }
    }, [pcDetails, pendingOverrides]);

    const [pendingRemovals, setPendingRemovals] = useState<string[]>([]);

    const handleRemoveOverride = useCallback(( ppName: string ) => {
        // If it's a pending (unsaved) move, just remove from pending overrides
        if ( ppName in pendingOverrides ) {
            const updated = { ...pendingOverrides };
            delete updated[ppName];
            setPendingOverrides(updated);
            return;
        }
        // If it's a saved override, mark as pending removal
        if ( !pendingRemovals.includes(ppName) ) {
            setPendingRemovals(prev => [...prev, ppName]);
        }
    }, [pendingOverrides, pendingRemovals]);

    const handleCreateNewPC = useCallback(() => {
        const trimmed = newPCName.trim();
        if (!trimmed) return;
        if (pcGroupedMappings[trimmed] || createdPCs.includes(trimmed)) {
            setSaveStatus({ success: false, message: `PC "${trimmed}" already exists.` });
            return;
        }
        setCreatedPCs(prev => [...prev, trimmed]);
        setShowNewPCInput(false);
        setNewPCName('');
        setSaveStatus({ success: true, message: `PC "${trimmed}" created. Drag process paths into it.` });
        // Scroll to the start so the new PC (first column) is visible
        setTimeout(() => {
            const wrapper = document.querySelector('.pc-columns-wrapper');
            if (wrapper) wrapper.scrollTo({ left: 0, behavior: 'smooth' });
        }, 100);
    }, [newPCName, pcGroupedMappings, createdPCs]);

    const handleSaveOverrides = useCallback(async () => {
        if (!siteId || !user) return;

        // Validate: any created PC must have at least 1 PP dragged into it
        for (const pc of createdPCs) {
            const hasPPs = Object.values(pendingOverrides).includes(pc);
            if (!hasPPs) {
                setSaveStatus({ success: false, message: `PC "${pc}" has no process paths. Drag at least one PP into it or remove it.` });
                return;
            }
        }

        // Must have at least one change (move or removal)
        if (Object.keys(pendingOverrides).length === 0 && pendingRemovals.length === 0) {
            return;
        }

        setIsSavingOverrides(true);
        setSaveStatus(null);
        try {
            // Process pending removals first
            for (const ppName of pendingRemovals) {
                await deletePCOverride({
                    fcName: siteId,
                    processPathName: ppName,
                    ticketUrl: overrideTicketUrl.trim(),
                    updatedBy: user.login || 'unknown',
                });
            }

            // Then process new overrides (if any)
            if (Object.keys(pendingOverrides).length > 0) {
                const response = await updatePCOverrides({
                    fcName: siteId,
                    overrides: pendingOverrides,
                    ticketUrl: overrideTicketUrl.trim(),
                    updatedBy: user.login || 'unknown',
                });
                if (!response.success) {
                    setSaveStatus({ success: false, message: response.message });
                    return;
                }
            }

            const hadRemovals = pendingRemovals.length > 0;
            setSaveStatus({ success: true, message: hadRemovals
                ? 'Changes saved successfully. Categorization regeneration triggered. Removed overrides may take up to 60 seconds to fully propagate — you may see stale data briefly.'
                : 'Changes saved successfully. Categorization regeneration triggered.' });
            setEditMode(false);
            setRecentlyRemovedPPs(prev => [...prev, ...pendingRemovals]);
            setPendingOverrides({});
            setPendingRemovals([]);
            setOverrideTicketUrl('');
            setCreatedPCs([]);
            refetchPCDetails();
            // For removals, poll until CPP-CDS propagates the revert
            if (hadRemovals) {
                let attempts = 0;
                const poll = setInterval(() => {
                    attempts++;
                    refetchPCDetails();
                    if (attempts >= 6) { // 60 seconds max
                        clearInterval(poll);
                        setRecentlyRemovedPPs([]);
                    }
                }, 10000);
            }
        } catch (e: any) {
            setSaveStatus({ success: false, message: e.message });
        } finally {
            setIsSavingOverrides(false);
        }
    }, [siteId, user, pendingOverrides, pendingRemovals, overrideTicketUrl, createdPCs, refetchPCDetails]);

    if (!user?.isDevAdmin && !user?.ppgAdmin) {
        return <div className="fc-config-page"><KatAlert variant="danger" header="Access Denied">Developer or PPG-admin access required.</KatAlert></div>;
    }

    if (!siteId) {
        return <div className="fc-config-page"><KatAlert variant="info" header="No Site Selected">Provide a site ID in the URL (e.g., /fcconfig/BFI4).</KatAlert></div>;
    }

    if (isLoading) {
        return <div className="fc-config-page"><KatSpinner /></div>;
    }

    if (error) {
        return <div className="fc-config-page"><KatAlert variant="danger" header="Error">{error.message}</KatAlert></div>;
    }

    const INHERIT_PICKING_VALUE = 'INHERIT_FROM_PICKING';
    const INHERIT_PICKING_LABEL = '— Inherits from PICKING —';

    // PC mapping types that are valid ONLY for HOV — these are shown in the HOV dropdown and
    // excluded from the PICKING dropdown. Add future HOV-only types here (single source of truth).
    const HOV_ONLY_MAPPING_TYPES = ['ONE_DC'];

    const hasPickingChanges = selectedMappingType !== (data?.productionCapabilityMappingType || '') ||
                              selectedGenerationType !== (data?.categorizationGenerationType || '');
    const hasHovChanges = selectedHovMappingType !== (data?.hovProductionCapabilityMappingType || 'INHERIT_FROM_PICKING') ||
                          selectedHovGenerationType !== (data?.hovCategorizationGenerationType || 'INHERIT_FROM_PICKING');
    const hasChanges = configTypeTab === 'PICKING' ? hasPickingChanges : hasHovChanges;
    const isTicketValid = ticketUrl.trim().startsWith('https://');
    const canSave = hasChanges && isTicketValid && !isSaving;

    // The backend returns full flag data; derive the FOCAL indicator and fcType the UI shows, preferring
    // the top-level response fields and falling back to the corresponding flag entries (beta-compatible).
    const gtrbFocalEnabled = gtrbData?.focalGtrbEnabled
        ?? (gtrbData?.flags?.find(f => f.key === 'CDS_R2F_ENABLED')?.enabled ?? false);
    const gtrbFcTypeValue = (gtrbData?.fcType
        || gtrbData?.flags?.find(f => f.key === 'CDS_FC_ATTRIBUTES')?.value
        || '');
    // Only show a concrete fcType in the dropdown when it is one of the selectable enum values.
    const gtrbFcTypeSelected = GTRB_FC_TYPES.includes(gtrbFcTypeValue) ? gtrbFcTypeValue : '';


    const handleSaveAndRegenerate = async () => {
        setIsSaving(true);
        setSaveStatus(null);
        try {
            const request: any = {
                fcName: siteId,
                ticketUrl: ticketUrl.trim(),
                updatedBy: user?.login || 'unknown',
            };
            if (configTypeTab === 'PICKING') {
                request.productionCapabilityMappingType = selectedMappingType;
                request.categorizationGenerationType = selectedGenerationType;
            } else {
                // Sentinel 'INHERIT_FROM_PICKING' means clear the HOV override (send nothing to backend)
                const hovMapping = selectedHovMappingType === 'INHERIT_FROM_PICKING' ? undefined : selectedHovMappingType;
                const hovGeneration = selectedHovGenerationType === 'INHERIT_FROM_PICKING' ? undefined : selectedHovGenerationType;
                request.hovProductionCapabilityMappingType = hovMapping;
                request.hovCategorizationGenerationType = hovGeneration;
            }
            const response = await updateFCConfig(request);
            setSaveStatus({ success: response?.success ?? false, message: response?.message ?? 'No response' });
            if (response?.success) refetch();
        } catch (e: any) {
            setSaveStatus({ success: false, message: e.message });
        } finally {
            setIsSaving(false);
        }
    };

    const handleRegenerate = async () => {
        setIsRegenerating(true);
        setSaveStatus(null);
        try {
            const response = await triggerCategorizationGeneration(siteId, configTypeTab);
            setSaveStatus({ success: response?.success ?? false, message: response?.message ?? 'No response' });
        } catch (e: any) {
            setSaveStatus({ success: false, message: e.message });
        } finally {
            setIsRegenerating(false);
        }
    };

    return (
        <div className="fc-config-page">
            <div className="fc-config-header">
                <h2>{siteId}</h2>
            </div>

            <div className="fc-config-active-config">
                <span className="fc-config-active-label">Active Configuration</span>
                <div className="fc-config-capsules">
                    <span className="fc-config-capsule">
                        <span className="fc-config-capsule-label">FOCAL enabled</span>
                        <span className={`fc-config-capsule-value gtrb-focal-value ${gtrbFocalEnabled ? 'gtrb-focal-true' : 'gtrb-focal-false'}`}>
                            {gtrbFocalEnabled ? 'true' : 'false'}
                        </span>
                    </span>
                    <span className="fc-config-capsule">
                        <span className="fc-config-capsule-label">PICKING Mapping</span>
                        <span className="fc-config-capsule-value">{data?.productionCapabilityMappingType}</span>
                    </span>
                    <span className="fc-config-capsule">
                        <span className="fc-config-capsule-label">PICKING Rules</span>
                        <span className="fc-config-capsule-value">{data?.categorizationGenerationType}</span>
                    </span>
                    <span className="fc-config-capsule">
                        <span className="fc-config-capsule-label">HOV Mapping</span>
                        <span className="fc-config-capsule-value">{data?.hovProductionCapabilityMappingType || 'inherits'}</span>
                    </span>
                    <span className="fc-config-capsule">
                        <span className="fc-config-capsule-label">HOV Rules</span>
                        <span className="fc-config-capsule-value">{data?.hovCategorizationGenerationType || 'inherits'}</span>
                    </span>
                    <span className="fc-config-capsule">
                        <span className="fc-config-capsule-label">PCs</span>
                        <span className="fc-config-capsule-value">{totalPCs}</span>
                    </span>
                    <span className="fc-config-capsule">
                        <span className="fc-config-capsule-label">PPs</span>
                        <span className="fc-config-capsule-value">{totalPPs}</span>
                    </span>
                </div>
            </div>

            {saveStatus && (
                <div className="fc-config-status-container">
                    <KatAlert
                        variant={saveStatus.success ? 'success' : 'danger'}
                        header={saveStatus.success ? 'Success' : 'Failed'}
                    >
                        {saveStatus.message}
                    </KatAlert>
                </div>
            )}

            <KatTabs selected={selectedTab} onChange={(e: any) => setSelectedTab(e.detail.selected)}>
                {user?.isDevAdmin && <KatTab tabId="configuration" label="Configuration" />}
                <KatTab tabId="pc-mapping" label="PC-PP Mapping" />
                {user?.isDevAdmin && <KatTab tabId="feature-flags" label="Feature Flags" />}
            </KatTabs>

            {user?.isDevAdmin && selectedTab === 'configuration' && (
                <div className="fc-config-tab-content">
                    <div className="fc-config-type-toggle">
                        <button
                            className={`fc-config-type-btn ${configTypeTab === 'PICKING' ? 'fc-config-type-btn-active' : ''}`}
                            onClick={() => setConfigTypeTab('PICKING')}
                        >PICKING</button>
                        <button
                            className={`fc-config-type-btn ${configTypeTab === 'HOV' ? 'fc-config-type-btn-active' : ''}`}
                            onClick={() => setConfigTypeTab('HOV')}
                        >HOV</button>
                    </div>

                    <div className="fc-config-form">
                        {configTypeTab === 'PICKING' && (
                            <>
                                <div className="fc-config-form-row">
                                    <label className="fc-config-form-label">PC Mapping Type</label>
                                    <KatDropdown
                                        value={selectedMappingType}
                                        options={(data?.availableProductionCapabilityMappingTypes || []).filter((t: string) => !HOV_ONLY_MAPPING_TYPES.includes(t)).map((t: string) => ({ name: t, value: t }))}
                                        onChange={(e: any) => setSelectedMappingType(e.detail?.value || e.target?.value || '')}
                                    />
                                </div>
                                <div className="fc-config-form-row">
                                    <label className="fc-config-form-label">Rule Generation Type</label>
                                    <KatDropdown
                                        value={selectedGenerationType}
                                        options={(data?.availableCategorizationGenerationTypes || []).map((t: string) => ({ name: t, value: t }))}
                                        onChange={(e: any) => setSelectedGenerationType(e.detail?.value || e.target?.value || '')}
                                    />
                                </div>
                            </>
                        )}
                        {configTypeTab === 'HOV' && (
                            <>
                                <div className="fc-config-form-row">
                                    <label className="fc-config-form-label">PC Mapping Type</label>
                                    <KatDropdown
                                        value={selectedHovMappingType}
                                        options={[
                                            { name: INHERIT_PICKING_LABEL, value: INHERIT_PICKING_VALUE },
                                            ...HOV_ONLY_MAPPING_TYPES.map((t: string) => ({ name: t, value: t })),
                                        ]}
                                        onChange={(e: any) => setSelectedHovMappingType(e.detail?.value ?? e.target?.value ?? '')}
                                    />
                                </div>
                                <div className="fc-config-form-row">
                                    <label className="fc-config-form-label">Rule Generation Type</label>
                                    <KatDropdown
                                        value={selectedHovGenerationType}
                                        options={[
                                            { name: INHERIT_PICKING_LABEL, value: INHERIT_PICKING_VALUE },
                                            ...(data?.availableCategorizationGenerationTypes || []).map((t: string) => ({ name: t, value: t })),
                                        ]}
                                        onChange={(e: any) => setSelectedHovGenerationType(e.detail?.value ?? e.target?.value ?? '')}
                                    />
                                </div>
                            </>
                        )}
                        <div className="fc-config-form-row">
                            <label className="fc-config-form-label">Ticket / MCM URL</label>
                            <KatInput
                                value={ticketUrl}
                                placeholder="https://..."
                                onChange={(e: any) => setTicketUrl(e.detail?.value || e.target?.value || '')}
                                onInput={(e: any) => setTicketUrl(e.detail?.value || e.target?.value || '')}
                            />
                        </div>
                        <div className="fc-config-form-actions">
                            <KatButton
                                variant="primary"
                                onClick={handleSaveAndRegenerate}
                                disabled={!canSave}
                                label={isSaving ? 'Saving...' : `Save & Regenerate ${configTypeTab}`}
                            />
                            <KatButton
                                variant="secondary"
                                onClick={handleRegenerate}
                                disabled={isRegenerating}
                                label={isRegenerating ? 'Regenerating...' : `Regenerate ${configTypeTab} Only`}
                            />
                        </div>
                        {!hasChanges && (
                            <p className="fc-config-hint">Change at least one value above to enable saving.</p>
                        )}
                    </div>
                </div>
            )}

            {user?.isDevAdmin && selectedTab === 'feature-flags' && (
                <div className="fc-config-tab-content">
                    <ErrorBoundary>
                        <div className="gtrb-config-panel">
                            <div className="gtrb-section-header">GTRB Status</div>
                        <div className="gtrb-readonly-note">
                            Current GTRB enablement state for this FC (MCM TM-141073).
                            Toggle a flag to enable/disable it for this FC (adds/removes the FC from the feature's
                            allow- or block-list without changing the global default); a ticket/MCM URL is required.
                        </div>
                        <div className="gtrb-active-config">
                            <span className="gtrb-active-config-item">
                                <span className="gtrb-active-config-label">FC type</span>
                                {gtrbPendingKey === 'CDS_FC_TYPE' ? (
                                    <KatSpinner size="small" />
                                ) : (
                                    <KatDropdown
                                        value={gtrbFcTypeSelected}
                                        options={GTRB_FC_TYPES.map(t => ({ name: t, value: t }))}
                                        disabled={!isGtrbTicketValid || setGtrbFlagMutation.isLoading}
                                        onChange={(e: any) => handleGtrbFcTypeChange(e.detail?.value || e.target?.value || '')}
                                    />
                                )}
                            </span>
                            {gtrbRowError && gtrbRowError.key === 'CDS_FC_TYPE' && (
                                <div className="gtrb-flag-row-error">{gtrbRowError.message}</div>
                            )}
                        </div>
                        <div className="gtrb-ticket-row">
                            <label className="fc-config-form-label">Ticket / MCM URL</label>
                            <KatInput
                                value={gtrbTicketUrl}
                                placeholder="https://..."
                                onChange={(e: any) => setGtrbTicketUrl(e.detail?.value || e.target?.value || '')}
                                onInput={(e: any) => setGtrbTicketUrl(e.detail?.value || e.target?.value || '')}
                            />
                            {!isGtrbTicketValid && (
                                <span className="gtrb-ticket-hint">Enter a valid https:// ticket URL to enable editing.</span>
                            )}
                        </div>
                        {gtrbLoading ? (
                            <div className="table-loading-container"><KatSpinner /></div>
                        ) : gtrbError ? (
                            <KatAlert variant="danger" header="Error">{gtrbError.message}</KatAlert>
                        ) : !gtrbData?.flags || gtrbData.flags.length === 0 ? (
                            <div className="table-empty-container"><p>No GTRB config data available for this FC.</p></div>
                        ) : (
                            <>
                                <div className="gtrb-flag-list">
                                    {gtrbData.flags.filter(flag => !GTRB_SSD_KEYS.has(flag.key) && !GTRB_HIDDEN_KEYS.has(flag.key)).map(flag => renderGtrbFlagRow(flag))}
                                </div>
                                {gtrbData.flags.some(flag => GTRB_SSD_KEYS.has(flag.key)) && (
                                    <div className="gtrb-ssd-group">
                                        <div className="gtrb-ssd-group-header">Only applicable if site is SSD</div>
                                        <div className="gtrb-flag-list">
                                            {gtrbData.flags.filter(flag => GTRB_SSD_KEYS.has(flag.key)).map(flag => renderGtrbFlagRow(flag))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        </div>
                    </ErrorBoundary>

                    <div className="gtrb-copy-config-launch">
                        <KatButton variant="secondary" label="Copy configuration from another FC" onClick={openCopyModal} />
                    </div>

                    <KatModal
                        visible={copyModalOpen}
                        title="Copy configuration from another FC"
                        onClose={closeCopyModal}
                    >
                        <div className="gtrb-copy-modal">
                            <div className="gtrb-readonly-note">
                                Load another FC's configuration, review the differences, then copy the selected scopes
                                onto <strong>{siteId}</strong>. Each setting is saved individually. One ticket/MCM URL
                                covers the whole copy.
                            </div>
                            <div className="gtrb-copy-controls">
                                <KatInput
                                    label="Source FC"
                                    value={copySourceFc}
                                    placeholder="e.g. BFI4"
                                    disabled={copyApplying}
                                    onChange={(e: any) => setCopySourceFc(e.detail?.value || e.target?.value || '')}
                                    onInput={(e: any) => setCopySourceFc(e.detail?.value || e.target?.value || '')}
                                />
                                <div className="gtrb-copy-scopes">
                                    <KatCheckbox checked={copyScopePicking} label="PICKING" disabled={copyApplying} onChange={(e: any) => setCopyScopePicking(e.detail?.checked ?? !copyScopePicking)} />
                                    <KatCheckbox checked={copyScopeHov} label="HOV" disabled={copyApplying} onChange={(e: any) => setCopyScopeHov(e.detail?.checked ?? !copyScopeHov)} />
                                    <KatCheckbox checked={copyScopeGtrb} label="GTRB flags" disabled={copyApplying} onChange={(e: any) => setCopyScopeGtrb(e.detail?.checked ?? !copyScopeGtrb)} />
                                    <KatCheckbox checked={copyScopeFcType} label="FC type" disabled={copyApplying} onChange={(e: any) => setCopyScopeFcType(e.detail?.checked ?? !copyScopeFcType)} />
                                </div>
                                <KatButton
                                    variant="secondary"
                                    label={copyLoading ? 'Loading…' : 'Show diff'}
                                    disabled={copyLoading || copyApplying || !copySourceFc.trim()}
                                    onClick={loadCopyDiff}
                                />
                            </div>
                            {copyError && <KatAlert variant="danger" header="Error">{copyError}</KatAlert>}
                            {copyStatus && <KatAlert variant="success" header="Copy complete">{copyStatus}</KatAlert>}
                            {copySourceConfig && (
                                <div className="gtrb-copy-diff">
                                    {copyDiff.rows.length === 0 ? (
                                        <p className="gtrb-copy-nodiff">No settings in the selected scopes for this source FC.</p>
                                    ) : (
                                        <table className="gtrb-copy-diff-table">
                                            <thead>
                                                <tr>
                                                    <th></th>
                                                    <th>Setting</th>
                                                    <th>{siteId} (current)</th>
                                                    <th>{copySourceFc.trim().toUpperCase()} (source)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {copyDiff.rows.map(row => {
                                                    const st = copyRowStatus[row.key];
                                                    return (
                                                        <tr key={row.key} className={row.changed ? '' : 'gtrb-copy-row-unchanged'}>
                                                            <td className="gtrb-copy-status-cell">
                                                                {st === 'done' ? <span className="gtrb-copy-tick">✓</span>
                                                                    : st === 'error' ? <span className="gtrb-copy-cross">✗</span>
                                                                        : st === 'saving' ? <KatSpinner size="small" />
                                                                            : !row.changed ? <span className="gtrb-copy-nochange">—</span>
                                                                                : ''}
                                                            </td>
                                                            <td>{row.label}
                                                                {st === 'error' && copyRowErrors[row.key] && (
                                                                    <div className="gtrb-flag-row-error">{copyRowErrors[row.key]}</div>
                                                                )}
                                                            </td>
                                                            <td className="gtrb-copy-target">{row.target}</td>
                                                            <td className="gtrb-copy-source">{row.source}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                    {copyDiff.skipped.length > 0 && (
                                        <div className="gtrb-copy-skipped">
                                            <strong>Skipped (not applicable on both FCs):</strong>
                                            <ul>{copyDiff.skipped.map((s, i) => <li key={i}>{s}</li>)}</ul>
                                        </div>
                                    )}
                                    {copyChangedRows.length > 0 && !copyStatus ? (
                                        <div className="gtrb-copy-apply-row">
                                            <KatInput
                                                label="Ticket / MCM URL"
                                                value={copyTicketUrl}
                                                placeholder="https://..."
                                                disabled={copyApplying}
                                                onChange={(e: any) => setCopyTicketUrl(e.detail?.value || e.target?.value || '')}
                                                onInput={(e: any) => setCopyTicketUrl(e.detail?.value || e.target?.value || '')}
                                            />
                                            <KatButton
                                                variant="primary"
                                                label={copyApplying ? 'Copying…' : `Confirm & copy ${copyChangedRows.length} change(s)`}
                                                disabled={copyApplying || !isCopyTicketValid}
                                                onClick={applyCopy}
                                            />
                                        </div>
                                    ) : (copyChangedRows.length === 0 && !copyStatus && (
                                        <p className="gtrb-copy-nodiff">All selected settings already match {copySourceFc.trim().toUpperCase()} — nothing to copy.</p>
                                    ))}
                                </div>
                            )}
                        </div>
                    </KatModal>
                </div>
            )}

            {selectedTab === 'pc-mapping' && (
                <div className="fc-config-tab-content">
                    {pcDetailsLoading ? (
                        <div className="table-loading-container"><KatSpinner /></div>
                    ) : totalPPs === 0 ? (
                        <div className="table-empty-container">
                            <p>No PC-PP mapping data available for this FC.</p>
                        </div>
                    ) : (
                        <div className="pc-mapping-container">
                            <div className="pc-mapping-toolbar">
                                <span>{totalPPs} process paths across {totalPCs} production capabilities</span>
                                <div className="pc-mapping-toolbar-actions">
                                    {!editMode ? (
                                        <KatButton variant="secondary" label="Edit Overrides" onClick={() => { setEditMode(true); setPendingOverrides({}); setSaveStatus(null); }} />
                                    ) : (
                                        <KatButton variant="secondary" label="Cancel" onClick={() => { setEditMode(false); setPendingOverrides({}); setPendingRemovals([]); setShowNewPCInput(false); setCreatedPCs([]); setSaveStatus(null); }} />
                                    )}
                                </div>
                            </div>
                            {editMode && (
                                <div className="pc-mapping-edit-panel">
                                    <div className="pc-mapping-edit-info">
                                        🖱️ <strong>Drag and drop</strong> process path chips between columns to reassign them to a different PC. Changes are previewed live and take effect after you click Save.
                                    </div>
                                    {(Object.keys(pendingOverrides).length > 0 || pendingRemovals.length > 0) && (
                                        <div className="pc-mapping-pending-changes">
                                            <strong>Pending changes ({Object.keys(pendingOverrides).length + pendingRemovals.length}):</strong>
                                            <ul>
                                                {Object.entries(pendingOverrides).map(([pp, pc]) => (
                                                    <li key={pp}>
                                                        {pp} → {pc}
                                                        <button className="pending-change-remove" onClick={() => { const updated = { ...pendingOverrides }; delete updated[pp]; setPendingOverrides(updated); }}>✕</button>
                                                    </li>
                                                ))}
                                                {pendingRemovals.map(pp => (
                                                    <li key={`rm-${pp}`} style={{color: '#d13212'}}>
                                                        {pp} → (remove override)
                                                        <button className="pending-change-remove" onClick={() => setPendingRemovals(prev => prev.filter(p => p !== pp))}>✕</button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    <div className="pc-mapping-edit-save-row">
                                        <KatInput
                                            value={overrideTicketUrl}
                                            placeholder="https://sim.amazon.com/issues/..."
                                            onChange={(e: any) => setOverrideTicketUrl(e.detail?.value || e.target?.value || '')}
                                            onInput={(e: any) => setOverrideTicketUrl(e.detail?.value || e.target?.value || '')}
                                        />
                                        <KatButton
                                            variant="primary"
                                            label={isSavingOverrides ? 'Saving...' : 'Save & Refresh Rules'}
                                            disabled={(Object.keys(pendingOverrides).length === 0 && pendingRemovals.length === 0) || !overrideTicketUrl.trim().startsWith('https://') || isSavingOverrides}
                                            onClick={handleSaveOverrides}
                                        />
                                    </div>
                                    <div className="pc-mapping-create-pc">
                                        {!showNewPCInput ? (
                                            <KatButton variant="secondary" label="+ Create New PC" onClick={() => setShowNewPCInput(true)} />
                                        ) : (
                                            <div className="pc-mapping-new-pc-row">
                                                <KatInput value={newPCName} placeholder="PC_NewName" onChange={(e: any) => setNewPCName(e.detail?.value || e.target?.value || '')} onInput={(e: any) => setNewPCName(e.detail?.value || e.target?.value || '')} />
                                                <KatButton variant="primary" label="Add" disabled={!newPCName.trim()} onClick={handleCreateNewPC} />
                                                <KatButton variant="secondary" label="Cancel" onClick={() => { setShowNewPCInput(false); setNewPCName(''); }} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className="pc-columns-scroll-hint">← Scroll horizontally to see all PCs →</div>
                            <div className="pc-columns-wrapper">
                                {Object.entries(editModeGroupedMappings)
                                    .sort(([a], [b]) => {
                                        // Newly created PCs always come first
                                        const aIsNew = createdPCs.includes(a);
                                        const bIsNew = createdPCs.includes(b);
                                        if (aIsNew && !bIsNew) return -1;
                                        if (!aIsNew && bIsNew) return 1;
                                        if (aIsNew && bIsNew) return createdPCs.indexOf(a) - createdPCs.indexOf(b);

                                        const order = ['PC_Singles', 'PC_Multis', 'PC_BOD', 'PC_PickToRebin'];
                                        const aIdx = order.indexOf(a);
                                        const bIdx = order.indexOf(b);
                                        if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
                                        if (aIdx !== -1) return -1;
                                        if (bIdx !== -1) return 1;
                                        if (a === 'PC_Other') return 1;
                                        if (b === 'PC_Other') return -1;
                                        if (a.startsWith('PC_Trans')) return 1;
                                        if (b.startsWith('PC_Trans')) return -1;
                                        return a.localeCompare(b);
                                    })
                                    .map(([pcName, ppList]) => (
                                    <PCColumn
                                        key={pcName}
                                        pcName={pcName}
                                        ppList={ppList}
                                        editMode={editMode}
                                        isNew={createdPCs.includes(pcName)}
                                        overrides={pendingOverrides}
                                        pendingRemovals={pendingRemovals}
                                        onDrop={handleDrop}
                                        onRemoveOverride={handleRemoveOverride}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FCConfigPage;
